(() => {
  const SELECTORS = {
    focusable: 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    menuButton: '[data-cmh-menu-open]',
    drawerLayer: '[data-cmh-drawer-layer]',
    drawer: '[data-cmh-drawer]',
    searchModal: '[data-cmh-search-modal]',
    searchDialog: '[data-cmh-search-dialog]',
    locationLayer: '[data-cmh-location-layer]',
    locationPanel: '[data-cmh-location-panel]',
    wishlistLayer: '[data-cmh-wishlist-layer]',
    wishlistDialog: '[data-cmh-wishlist-dialog]'
  };

  class MediumStoreHeader extends HTMLElement {
    constructor() {
      super();
      this.searchTimers = new WeakMap();
      this.searchRequests = new WeakMap();
      this.locationTimer = null;
      this.locationRequest = null;
      this.placeholderTimer = null;
      this.placeholderIndex = 0;
      this.lastFocused = null;
      this.voiceRecognition = null;
      this.voiceButton = null;
      this.wishlistCache = new Map();
      this.wishlistRenderToken = 0;
      this.onRootClick = this.onRootClick.bind(this);
      this.onRootInput = this.onRootInput.bind(this);
      this.onRootSubmit = this.onRootSubmit.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.refreshCart = this.refreshCart.bind(this);
      this.onWishlistUpdated = this.onWishlistUpdated.bind(this);
      this.onStorage = this.onStorage.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener('click', this.onRootClick);
      this.addEventListener('input', this.onRootInput);
      this.addEventListener('submit', this.onRootSubmit);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('cart:updated', this.refreshCart);
      document.addEventListener('cart:refresh', this.refreshCart);
      document.addEventListener('cart-drawer:updated', this.refreshCart);
      document.addEventListener('rudraksha:wishlist-updated', this.onWishlistUpdated);
      window.addEventListener('storage', this.onStorage);
      this.restoreLocation();
      this.initializeCollectionBrowser();
      this.initializeRotatingPlaceholders();
      this.initializeVoiceSearch();
      this.refreshWishlistCount();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onRootClick);
      this.removeEventListener('input', this.onRootInput);
      this.removeEventListener('submit', this.onRootSubmit);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('cart:updated', this.refreshCart);
      document.removeEventListener('cart:refresh', this.refreshCart);
      document.removeEventListener('cart-drawer:updated', this.refreshCart);
      document.removeEventListener('rudraksha:wishlist-updated', this.onWishlistUpdated);
      window.removeEventListener('storage', this.onStorage);
      window.clearTimeout(this.locationTimer);
      window.clearInterval(this.placeholderTimer);
      if (this.locationRequest) this.locationRequest.abort();
      this.searchRequests.forEach?.((request) => request.abort());
      if (this.voiceRecognition) this.voiceRecognition.abort();
    }

    onRootClick(event) {
      const wishlistOpen = event.target.closest('[data-cmh-wishlist-open]');
      if (wishlistOpen) {
        event.preventDefault();
        this.openWishlist(wishlistOpen);
        return;
      }

      if (event.target.closest('[data-cmh-wishlist-close]')) {
        event.preventDefault();
        this.closeWishlist();
        return;
      }

      const wishlistRemove = event.target.closest('[data-cmh-wishlist-remove]');
      if (wishlistRemove) {
        event.preventDefault();
        this.removeWishlistItem(wishlistRemove);
        return;
      }

      const menuOpen = event.target.closest('[data-cmh-menu-open]');
      if (menuOpen) {
        event.preventDefault();
        this.openDrawer(menuOpen);
        return;
      }

      if (event.target.closest('[data-cmh-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const searchOpen = event.target.closest('[data-cmh-search-open]');
      if (searchOpen) {
        event.preventDefault();
        this.openSearch(searchOpen);
        return;
      }

      if (event.target.closest('[data-cmh-search-close]')) {
        event.preventDefault();
        this.closeSearch();
        return;
      }

      const locationOpen = event.target.closest('[data-cmh-location-open]');
      if (locationOpen) {
        event.preventDefault();
        this.openLocation(locationOpen);
        return;
      }

      if (event.target.closest('[data-cmh-location-close]')) {
        event.preventDefault();
        this.closeLocation();
        return;
      }

      const currentLocation = event.target.closest('[data-cmh-current-location]');
      if (currentLocation) {
        event.preventDefault();
        this.useCurrentLocation(currentLocation);
        return;
      }

      const locationChoice = event.target.closest('[data-cmh-location-choice]');
      if (locationChoice) {
        event.preventDefault();
        this.selectLocation({
          label: locationChoice.dataset.label,
          latitude: locationChoice.dataset.latitude,
          longitude: locationChoice.dataset.longitude
        });
        return;
      }

      const collectionTrigger = event.target.closest('[data-cmh-collection-trigger]');
      if (collectionTrigger) {
        event.preventDefault();
        this.activateCollection(collectionTrigger);
        return;
      }

      const scrollButton = event.target.closest('[data-cmh-products-scroll]');
      if (scrollButton) {
        event.preventDefault();
        this.scrollProducts(scrollButton);
        return;
      }

      const voiceButton = event.target.closest('[data-cmh-voice-search]');
      if (voiceButton) {
        event.preventDefault();
        this.toggleVoiceSearch(voiceButton);
      }
    }

    onRootInput(event) {
      if (event.target.matches('[data-cmh-search-input]')) {
        this.queueProductSearch(event.target);
      }

      if (event.target.matches('[data-cmh-location-input]')) {
        this.queueLocationSearch(event.target.value);
      }
    }

    onRootSubmit(event) {
      if (!event.target.matches('[data-cmh-location-form]')) return;
      event.preventDefault();
      const input = event.target.querySelector('[data-cmh-location-input]');
      this.searchLocations(input?.value || '');
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.hideSearchResults();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        if (this.isOpen(SELECTORS.wishlistLayer)) this.closeWishlist();
        else if (this.isOpen(SELECTORS.locationLayer)) this.closeLocation();
        else if (this.isOpen(SELECTORS.searchModal)) this.closeSearch();
        else if (this.isOpen(SELECTORS.drawerLayer)) this.closeDrawer();
        else this.hideSearchResults();
        return;
      }

      if (event.key !== 'Tab') return;
      const container = this.activeDialog();
      if (!container) return;
      const focusable = [...container.querySelectorAll(SELECTORS.focusable)].filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    activeDialog() {
      if (this.isOpen(SELECTORS.wishlistLayer)) return this.querySelector(SELECTORS.wishlistDialog);
      if (this.isOpen(SELECTORS.locationLayer)) return this.querySelector(SELECTORS.locationPanel);
      if (this.isOpen(SELECTORS.searchModal)) return this.querySelector(SELECTORS.searchDialog);
      if (this.isOpen(SELECTORS.drawerLayer)) return this.querySelector(SELECTORS.drawer);
      return null;
    }

    isOpen(selector) {
      return this.querySelector(selector)?.classList.contains('is-open');
    }

    rememberFocus(element) {
      this.lastFocused = element || document.activeElement;
    }

    restoreFocus() {
      const target = this.lastFocused;
      this.lastFocused = null;
      if (target && document.contains(target)) target.focus({ preventScroll: true });
    }

    updateScrollLock() {
      const hasOpenLayer = this.isOpen(SELECTORS.drawerLayer) || this.isOpen(SELECTORS.searchModal) || this.isOpen(SELECTORS.locationLayer) || this.isOpen(SELECTORS.wishlistLayer);
      document.documentElement.classList.toggle('cmh-scroll-locked', hasOpenLayer);
    }

    closeOtherLayers(except) {
      if (except !== 'drawer') this.closeDrawer(false);
      if (except !== 'search') this.closeSearch(false);
      if (except !== 'location') this.closeLocation(false);
      if (except !== 'wishlist') this.closeWishlist(false);
    }

    openDrawer(trigger) {
      this.closeOtherLayers('drawer');
      this.rememberFocus(trigger);
      const layer = this.querySelector(SELECTORS.drawerLayer);
      const drawer = this.querySelector(SELECTORS.drawer);
      if (!layer || !drawer) return;
      layer.classList.add('is-open');
      layer.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('[data-cmh-menu-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.updateScrollLock();
      window.requestAnimationFrame(() => drawer.focus({ preventScroll: true }));
    }

    closeDrawer(restore = true) {
      const layer = this.querySelector(SELECTORS.drawerLayer);
      if (!layer?.classList.contains('is-open')) return;
      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
      this.querySelectorAll('[data-cmh-menu-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.updateScrollLock();
      if (restore) this.restoreFocus();
    }

    openSearch(trigger) {
      this.closeOtherLayers('search');
      this.rememberFocus(trigger);
      const modal = this.querySelector(SELECTORS.searchModal);
      const dialog = this.querySelector(SELECTORS.searchDialog);
      if (!modal || !dialog) return;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('[data-cmh-search-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.updateScrollLock();
      window.requestAnimationFrame(() => dialog.querySelector('[data-cmh-search-input]')?.focus());
    }

    closeSearch(restore = true) {
      const modal = this.querySelector(SELECTORS.searchModal);
      if (!modal?.classList.contains('is-open')) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      this.querySelectorAll('[data-cmh-search-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.hideSearchResults(modal);
      this.updateScrollLock();
      if (restore) this.restoreFocus();
    }

    openLocation(trigger) {
      this.closeOtherLayers('location');
      this.rememberFocus(trigger);
      const layer = this.querySelector(SELECTORS.locationLayer);
      const panel = this.querySelector(SELECTORS.locationPanel);
      if (!layer || !panel) return;
      this.positionLocationPanel(trigger);
      layer.classList.add('is-open');
      layer.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('[data-cmh-location-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.updateScrollLock();
      window.requestAnimationFrame(() => panel.focus({ preventScroll: true }));
    }

    closeLocation(restore = true) {
      const layer = this.querySelector(SELECTORS.locationLayer);
      if (!layer?.classList.contains('is-open')) return;
      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
      this.querySelectorAll('[data-cmh-location-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.updateScrollLock();
      if (restore) this.restoreFocus();
    }

    openWishlist(trigger) {
      this.closeOtherLayers('wishlist');
      this.rememberFocus(trigger);
      const layer = this.querySelector(SELECTORS.wishlistLayer);
      const dialog = this.querySelector(SELECTORS.wishlistDialog);
      if (!layer || !dialog) return;
      layer.classList.add('is-open');
      layer.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('[data-cmh-wishlist-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.updateScrollLock();
      this.renderWishlist();
      window.requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
    }

    closeWishlist(restore = true) {
      const layer = this.querySelector(SELECTORS.wishlistLayer);
      if (!layer?.classList.contains('is-open')) return;
      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
      this.querySelectorAll('[data-cmh-wishlist-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.wishlistRenderToken += 1;
      this.updateScrollLock();
      if (restore) this.restoreFocus();
    }

    positionLocationPanel(trigger) {
      if (!trigger || window.matchMedia('(max-width: 989px)').matches) return;
      const rect = trigger.getBoundingClientRect();
      const panelWidth = Math.min(460, window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - panelWidth - 12));
      this.style.setProperty('--cmh-location-top', `${Math.round(rect.bottom + 9)}px`);
      this.style.setProperty('--cmh-location-left', `${Math.round(left)}px`);
    }

    initializeCollectionBrowser() {
      const active = this.querySelector('[data-cmh-collection-trigger].is-active') || this.querySelector('[data-cmh-collection-trigger]');
      if (active) this.activateCollection(active);
      this.querySelectorAll('[data-cmh-collection-trigger]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => {
          if (window.matchMedia('(min-width: 990px)').matches) this.activateCollection(trigger);
        });
        trigger.addEventListener('focus', () => this.activateCollection(trigger));
      });
    }

    activateCollection(trigger) {
      const panelId = trigger.dataset.cmhPanel;
      if (!panelId) return;
      this.querySelectorAll('[data-cmh-collection-trigger]').forEach((item) => {
        const active = item === trigger;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-cmh-collection-panel]').forEach((panel) => {
        const active = panel.id === panelId;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    }

    scrollProducts(button) {
      const panel = button.closest('[data-cmh-collection-panel]');
      const viewport = panel?.querySelector('[data-cmh-products-viewport]');
      if (!viewport) return;
      const direction = button.dataset.cmhProductsScroll === 'previous' ? -1 : 1;
      viewport.scrollBy({ left: direction * Math.max(240, viewport.clientWidth * .82), behavior: 'smooth' });
    }

    initializeRotatingPlaceholders() {
      if (this.dataset.rotatePlaceholders !== 'true') return;
      const source = this.querySelector('[data-cmh-search-suggestions]');
      let suggestions = [];
      try {
        suggestions = JSON.parse(source?.textContent || '[]').filter(Boolean);
      } catch (error) {
        suggestions = [];
      }
      if (!suggestions.length) return;
      this.placeholderSuggestions = suggestions;
      this.updateRotatingPlaceholder();
      const delay = Math.max(2000, Number(this.dataset.placeholderHold) || 4000);
      this.placeholderTimer = window.setInterval(() => {
        this.placeholderIndex = (this.placeholderIndex + 1) % suggestions.length;
        this.updateRotatingPlaceholder();
      }, delay);
    }

    updateRotatingPlaceholder() {
      const productName = this.placeholderSuggestions?.[this.placeholderIndex];
      if (!productName) return;
      const prefix = (this.dataset.searchPrefix || 'Search for').trim();
      const placeholder = `${prefix} “${productName}”`;
      this.querySelectorAll('[data-cmh-search-input]').forEach((input) => {
        input.placeholder = placeholder;
      });
    }

    queueProductSearch(input) {
      const scope = input.closest('[data-search-scope]');
      if (!scope) return;
      window.clearTimeout(this.searchTimers.get(scope));
      const query = input.value.trim();
      if (query.length < 2) {
        this.hideSearchResults(scope);
        this.setSearchStatus(scope, '');
        return;
      }
      this.searchTimers.set(scope, window.setTimeout(() => this.searchProducts(query, scope), 220));
    }

    async searchProducts(query, scope) {
      const previous = this.searchRequests.get(scope);
      if (previous) previous.abort();
      const request = new AbortController();
      this.searchRequests.set(scope, request);
      this.setSearchStatus(scope, `Searching for ${query}`);
      try {
        const base = (this.dataset.searchUrl || '/search/suggest').replace(/\.json(?:\?.*)?$/, '');
        const url = `${base}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${this.dataset.searchLimit || 6}&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search request failed');
        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        this.renderSearchResults(scope, products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderSearchResults(scope, [], query, true);
      }
    }

    renderSearchResults(scope, products, query, failed = false) {
      const container = scope.querySelector('[data-cmh-search-results]');
      if (!container) return;
      container.replaceChildren();
      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'custom-medium-header__search-empty';
        empty.textContent = failed ? 'Search is temporarily unavailable. Press Enter to view all results.' : `No products found for “${query}”. Press Enter to view all results.`;
        container.append(empty);
        container.hidden = false;
        this.setSearchStatus(scope, failed ? 'Product search unavailable' : 'No matching products found');
        return;
      }

      products.forEach((product) => container.append(this.createProductResult(product)));
      container.hidden = false;
      this.setSearchStatus(scope, `${products.length} product${products.length === 1 ? '' : 's'} found`);
    }

    createProductResult(product) {
      const link = document.createElement('a');
      link.className = 'custom-medium-header__search-result';
      link.href = product.url || '#';

      const imageUrl = product.image || product.featured_image?.url || product.featured_image;
      if (imageUrl && typeof imageUrl === 'string') {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        link.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'custom-medium-header__search-result-placeholder';
        link.append(placeholder);
      }

      const copy = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = product.title || 'Product';
      copy.append(title);
      if (product.vendor) {
        const vendor = document.createElement('small');
        vendor.textContent = product.vendor;
        copy.append(vendor);
      }
      link.append(copy);

      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      link.append(arrow);
      return link;
    }

    setSearchStatus(scope, message) {
      const status = scope.querySelector('[data-cmh-search-status]');
      if (status) status.textContent = message;
    }

    hideSearchResults(within = this) {
      within.querySelectorAll('[data-cmh-search-results]').forEach((container) => {
        container.hidden = true;
      });
    }

    initializeVoiceSearch() {
      if (this.dataset.voiceEnabled !== 'true') return;
      const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!Recognition) {
        this.querySelectorAll('[data-cmh-voice-search]').forEach((button) => {
          button.disabled = true;
          button.title = 'Voice search is not supported by this browser';
        });
        return;
      }
      this.SpeechRecognition = Recognition;
    }

    toggleVoiceSearch(button) {
      if (!this.SpeechRecognition) return;
      if (this.voiceRecognition) {
        this.voiceRecognition.stop();
        return;
      }

      const scope = button.closest('[data-search-scope]');
      const input = scope?.querySelector('[data-cmh-search-input]');
      if (!scope || !input) return;
      const recognition = new this.SpeechRecognition();
      recognition.lang = this.dataset.voiceLanguage || 'en-IN';
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      this.voiceRecognition = recognition;
      this.voiceButton = button;

      recognition.onstart = () => {
        button.classList.add('is-listening');
        button.setAttribute('aria-pressed', 'true');
        this.setSearchStatus(scope, 'Listening. Say a product name.');
      };

      recognition.onresult = (event) => {
        let transcript = '';
        let isFinal = false;
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          transcript += event.results[index][0].transcript;
          isFinal = isFinal || event.results[index].isFinal;
        }
        input.value = transcript.trim();
        input.dispatchEvent(new Event('input', { bubbles: true }));
        if (isFinal && this.dataset.voiceAutoSubmit === 'true') {
          window.setTimeout(() => scope.querySelector('[data-cmh-search-form]')?.requestSubmit(), 250);
        }
      };

      recognition.onerror = (event) => {
        const message = event.error === 'not-allowed' ? 'Microphone access was blocked. Allow microphone access to use voice search.' : 'I could not hear that. Please try again.';
        this.setSearchStatus(scope, message);
      };

      recognition.onend = () => {
        button.classList.remove('is-listening');
        button.setAttribute('aria-pressed', 'false');
        this.voiceRecognition = null;
        this.voiceButton = null;
      };

      try {
        recognition.start();
      } catch (error) {
        this.voiceRecognition = null;
      }
    }

    queueLocationSearch(query) {
      window.clearTimeout(this.locationTimer);
      const value = query.trim();
      const input = this.querySelector('[data-cmh-location-input]');
      if (value.length < 2) {
        this.clearLocationResults();
        input?.setAttribute('aria-expanded', 'false');
        return;
      }
      this.locationTimer = window.setTimeout(() => this.searchLocations(value), 320);
    }

    async searchLocations(query) {
      const value = query.trim();
      if (value.length < 2) return;
      if (this.locationRequest) this.locationRequest.abort();
      this.locationRequest = new AbortController();
      this.setLocationStatus('Searching locations…');
      try {
        const endpoint = (this.dataset.locationEndpoint || 'https://photon.komoot.io').replace(/\/$/, '');
        const response = await fetch(`${endpoint}/api/?q=${encodeURIComponent(value)}&limit=10&lang=en`, { signal: this.locationRequest.signal });
        if (!response.ok) throw new Error('Location search failed');
        const payload = await response.json();
        let features = payload.features || [];
        const country = (this.dataset.locationCountry || '').toLowerCase();
        if (country) features = features.filter((feature) => !feature.properties?.countrycode || feature.properties.countrycode.toLowerCase() === country);
        this.renderLocationResults(features.slice(0, 7));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.clearLocationResults();
        this.setLocationStatus('Location search is unavailable. Please try again.');
      }
    }

    renderLocationResults(features) {
      const results = this.querySelector('[data-cmh-location-results]');
      const input = this.querySelector('[data-cmh-location-input]');
      if (!results) return;
      results.replaceChildren();
      if (!features.length) {
        results.hidden = true;
        input?.setAttribute('aria-expanded', 'false');
        this.setLocationStatus('No matching locations found.');
        return;
      }

      features.forEach((feature) => {
        const properties = feature.properties || {};
        const coordinates = feature.geometry?.coordinates || [];
        const label = this.formatLocation(properties);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'custom-medium-header__location-result';
        button.dataset.cmhLocationChoice = '';
        button.dataset.label = label;
        button.dataset.longitude = coordinates[0] ?? '';
        button.dataset.latitude = coordinates[1] ?? '';
        button.setAttribute('role', 'option');

        const strong = document.createElement('strong');
        strong.textContent = properties.name || properties.city || properties.locality || label;
        const small = document.createElement('small');
        small.textContent = [properties.district, properties.state, properties.country].filter(Boolean).join(', ');
        button.append(strong, small);
        results.append(button);
      });
      results.hidden = false;
      input?.setAttribute('aria-expanded', 'true');
      this.setLocationStatus(`${features.length} locations found.`);
    }

    formatLocation(properties) {
      const primary = properties.name || properties.city || properties.locality || properties.district || 'Selected location';
      const secondary = properties.city && properties.city !== primary ? properties.city : properties.state;
      return [primary, secondary].filter(Boolean).join(', ');
    }

    clearLocationResults() {
      const results = this.querySelector('[data-cmh-location-results]');
      if (results) {
        results.replaceChildren();
        results.hidden = true;
      }
    }

    setLocationStatus(message) {
      const status = this.querySelector('[data-cmh-location-status]');
      if (status) status.textContent = message;
    }

    useCurrentLocation(button) {
      if (!navigator.geolocation) {
        this.setLocationStatus('Current location is not supported by this browser.');
        return;
      }
      button.disabled = true;
      button.classList.add('is-loading');
      this.setLocationStatus('Finding your current location…');
      navigator.geolocation.getCurrentPosition(
        (position) => this.reverseGeocode(position.coords.latitude, position.coords.longitude, button),
        (error) => {
          button.disabled = false;
          button.classList.remove('is-loading');
          this.setLocationStatus(error.code === 1 ? 'Location permission was denied. Search for your city instead.' : 'Unable to find your current location. Please try again.');
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 300000 }
      );
    }

    async reverseGeocode(latitude, longitude, button) {
      try {
        const endpoint = (this.dataset.locationEndpoint || 'https://photon.komoot.io').replace(/\/$/, '');
        const response = await fetch(`${endpoint}/reverse?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}&lang=en`);
        if (!response.ok) throw new Error('Reverse geocoding failed');
        const payload = await response.json();
        const properties = payload.features?.[0]?.properties || {};
        this.selectLocation({ label: this.formatLocation(properties), latitude, longitude });
      } catch (error) {
        this.selectLocation({ label: 'Current location', latitude, longitude });
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    selectLocation(location) {
      if (!location?.label) return;
      this.querySelectorAll('[data-cmh-location-label]').forEach((label) => {
        label.textContent = location.label;
      });
      try {
        localStorage.setItem('local-pandit:selected-location:v1', JSON.stringify(location));
      } catch (error) {
        // The selected location still works for this page when storage is unavailable.
      }
      this.clearLocationResults();
      this.setLocationStatus(`Location set to ${location.label}.`);
      this.dispatchEvent(new CustomEvent('location:changed', { bubbles: true, detail: location }));
      window.setTimeout(() => this.closeLocation(), 180);
    }

    restoreLocation() {
      try {
        const location = JSON.parse(localStorage.getItem('local-pandit:selected-location:v1') || 'null');
        if (!location?.label) return;
        this.querySelectorAll('[data-cmh-location-label]').forEach((label) => {
          label.textContent = location.label;
        });
      } catch (error) {
        // Ignore malformed or unavailable local storage.
      }
    }

    async refreshCart() {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        this.updateCartCount(Number(cart.item_count) || 0);
      } catch (error) {
        // Theme cart events can still update the count through the rendered cart bubble.
      }
    }

    updateCartCount(count) {
      this.querySelectorAll('.custom-medium-header__cart-link').forEach((link) => {
        let bubble = link.querySelector('.custom-medium-header__count');
        if (count > 0 && !bubble) {
          bubble = document.createElement('span');
          bubble.className = 'custom-medium-header__count';
          bubble.setAttribute('aria-hidden', 'true');
          link.append(bubble);
        }
        if (bubble) {
          bubble.textContent = String(count);
          bubble.hidden = count === 0;
        }
        link.setAttribute('aria-label', count ? `Cart: ${count} items` : 'Cart');
      });
    }

    getWishlistHandles() {
      const key = this.dataset.wishlistStorageKey || 'shopify-wishlist';
      try {
        const raw = localStorage.getItem(key) || '';
        let handles = [];
        if (raw.trim().startsWith('[')) {
          const parsed = JSON.parse(raw);
          handles = Array.isArray(parsed) ? parsed.map((item) => typeof item === 'string' ? item : item?.handle) : [];
        } else {
          handles = raw.split(',');
        }
        return [...new Set(handles.map((handle) => String(handle || '').trim()).filter(Boolean))];
      } catch (error) {
        return [];
      }
    }

    setWishlistHandles(handles) {
      const key = this.dataset.wishlistStorageKey || 'shopify-wishlist';
      try {
        if (handles.length) localStorage.setItem(key, handles.join(','));
        else localStorage.removeItem(key);
        return true;
      } catch (error) {
        return false;
      }
    }

    refreshWishlistCount(handles = this.getWishlistHandles()) {
      const count = handles.length;
      this.querySelectorAll('[data-cmh-wishlist-count]').forEach((bubble) => {
        bubble.textContent = String(count);
        bubble.hidden = count === 0;
      });
      const summary = this.querySelector('[data-cmh-wishlist-summary]');
      if (summary) summary.textContent = `${count} saved item${count === 1 ? '' : 's'}`;
    }

    onWishlistUpdated() {
      const handles = this.getWishlistHandles();
      this.refreshWishlistCount(handles);
      if (this.isOpen(SELECTORS.wishlistLayer)) this.renderWishlist(handles);
    }

    onStorage(event) {
      if (event.key !== (this.dataset.wishlistStorageKey || 'shopify-wishlist')) return;
      this.onWishlistUpdated();
    }

    async renderWishlist(handles = this.getWishlistHandles()) {
      const list = this.querySelector('[data-cmh-wishlist-list]');
      const empty = this.querySelector('[data-cmh-wishlist-empty]');
      const loading = this.querySelector('[data-cmh-wishlist-loading]');
      if (!list || !empty || !loading) return;

      const token = ++this.wishlistRenderToken;
      this.refreshWishlistCount(handles);
      list.replaceChildren();
      empty.hidden = handles.length > 0;
      loading.hidden = handles.length === 0;
      if (!handles.length) return;

      const products = await Promise.all(handles.map((handle) => this.fetchWishlistProduct(handle)));
      if (token !== this.wishlistRenderToken) return;

      loading.hidden = true;
      const fragment = document.createDocumentFragment();
      products.forEach((product, index) => {
        fragment.append(this.createWishlistCard(product || this.unavailableWishlistProduct(handles[index])));
      });
      list.append(fragment);
      empty.hidden = products.length > 0;
    }

    async fetchWishlistProduct(handle) {
      if (this.wishlistCache.has(handle)) return this.wishlistCache.get(handle);
      const root = `${this.dataset.rootUrl || '/'}`.replace(/\/?$/, '/');
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        product.handle = product.handle || handle;
        product.url = product.url || `${root}products/${encodeURIComponent(handle)}`;
        this.wishlistCache.set(handle, product);
        return product;
      } catch (error) {
        return null;
      }
    }

    unavailableWishlistProduct(handle) {
      const root = `${this.dataset.rootUrl || '/'}`.replace(/\/?$/, '/');
      return {
        handle,
        title: handle.split('-').filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Saved product',
        url: `${root}products/${encodeURIComponent(handle)}`,
        unavailable: true
      };
    }

    createWishlistCard(product) {
      const card = document.createElement('article');
      card.className = 'custom-medium-header__wishlist-card';
      card.dataset.productHandle = product.handle || '';

      const media = document.createElement('a');
      media.className = 'custom-medium-header__wishlist-card-media';
      media.href = product.url || '#';
      media.setAttribute('aria-label', `View ${product.title || 'saved product'}`);
      const imageUrl = typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.url || product.images?.[0]?.src || product.images?.[0];
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = product.title || '';
        image.loading = 'lazy';
        media.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'custom-medium-header__wishlist-card-placeholder';
        placeholder.textContent = '♡';
        media.append(placeholder);
      }
      const savedBadge = document.createElement('span');
      savedBadge.className = 'custom-medium-header__wishlist-card-badge';
      savedBadge.textContent = 'Saved';
      media.append(savedBadge);
      card.append(media);

      const content = document.createElement('div');
      content.className = 'custom-medium-header__wishlist-card-content';
      if (product.vendor || product.type) {
        const meta = document.createElement('span');
        meta.className = 'custom-medium-header__wishlist-card-meta';
        meta.textContent = product.type || product.vendor;
        content.append(meta);
      }

      const title = document.createElement('a');
      title.className = 'custom-medium-header__wishlist-card-title';
      title.href = product.url || '#';
      title.textContent = product.title || 'Saved product';
      content.append(title);

      if (product.unavailable) {
        const unavailable = document.createElement('span');
        unavailable.className = 'custom-medium-header__wishlist-card-unavailable';
        unavailable.textContent = 'Product details are temporarily unavailable';
        content.append(unavailable);
      } else if (Number.isFinite(Number(product.price))) {
        const price = document.createElement('strong');
        price.className = 'custom-medium-header__wishlist-card-price';
        price.textContent = this.formatMoney(product.price);
        content.append(price);
      }

      const actions = document.createElement('div');
      actions.className = 'custom-medium-header__wishlist-card-actions';
      const view = document.createElement('a');
      view.href = product.url || '#';
      view.textContent = 'View product';
      actions.append(view);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.cmhWishlistRemove = '';
      remove.dataset.productHandle = product.handle || '';
      remove.dataset.productTitle = product.title || 'Product';
      remove.setAttribute('aria-label', `Remove ${product.title || 'product'} from wishlist`);
      remove.textContent = 'Remove';
      actions.append(remove);
      content.append(actions);
      card.append(content);
      return card;
    }

    formatMoney(cents) {
      const currency = this.dataset.currencyCode || 'INR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency,
          maximumFractionDigits: 2
        }).format(Number(cents) / 100);
      } catch (error) {
        return `${currency} ${(Number(cents) / 100).toFixed(2)}`;
      }
    }

    removeWishlistItem(button) {
      const handle = button.dataset.productHandle;
      if (!handle) return;
      const handles = this.getWishlistHandles().filter((item) => item !== handle);
      if (!this.setWishlistHandles(handles)) return;
      this.wishlistCache.delete(handle);
      this.syncWishlistButtons(handle, false);
      const status = this.querySelector('[data-cmh-wishlist-status]');
      if (status) status.textContent = `${button.dataset.productTitle || 'Product'} removed from your wishlist.`;
      document.dispatchEvent(new CustomEvent('rudraksha:wishlist-updated', { detail: { count: handles.length } }));
    }

    syncWishlistButtons(handle, isActive) {
      const escapedHandle = window.CSS?.escape ? CSS.escape(handle) : handle.replace(/["\\]/g, '\\$&');
      document.querySelectorAll(`[data-mcg-wishlist][data-product-handle="${escapedHandle}"], [data-rcc-wishlist][data-product-handle="${escapedHandle}"]`).forEach((button) => {
        const title = button.dataset.productTitle || 'Product';
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
        button.setAttribute('aria-label', `${isActive ? 'Remove' : 'Add'} ${title} ${isActive ? 'from' : 'to'} wishlist`);
      });
    }
  }

  if (!customElements.get('medium-store-header')) {
    customElements.define('medium-store-header', MediumStoreHeader);
  }
})();
